import { z } from 'zod';
import { CountryCode, isoDay } from './values.js';
/**
 * Whether a person is politically exposed (§ 1 Abs. 12-14, § 10 Abs. 1 Nr. 4 GwG), as the person
 * states it (ADR-029). Kanos records the statement and derives nothing from it: what a statement
 * means for the due diligence, the rules of the responsible experts will say. Until then an
 * unanswered question is "not stated", never "no".
 */
/** Holds an office itself, is a family member of one who does, or is known to be close to one. */
export const PepStatus = z
    .enum(['none', 'pep', 'family_member', 'close_associate'])
    .meta({ id: 'PepStatus' });
/**
 * The kind of prominent public function, as § 1 Abs. 12 Nr. 1 GwG lists them; `other` for an
 * office of the EU list of prominent public functions (C/2023/724) beyond them.
 */
export const PepFunction = z
    .enum([
    'government',
    'parliament',
    'party_leadership',
    'supreme_court',
    'court_of_auditors',
    'central_bank',
    'diplomacy',
    'state_enterprise',
    'international_organisation',
    'other',
])
    .meta({ id: 'PepFunction' });
const words = (max) => z.string().trim().min(1).max(max).nullable();
/**
 * One statement: whether, which office and where - and, for a family member or a close associate,
 * of whom. Every part may stay empty; only a "no" or nothing at all leaves no details.
 */
export const PepStatement = z
    .object({
    status: PepStatus.nullable(),
    function: PepFunction.nullable(),
    /** The office in the person's words, e.g. "Mitglied des Landtags Nordrhein-Westfalen". */
    office: words(200),
    /** Where the office is held: ISO 3166-1 alpha-2, or `EU` for an institution of the Union. */
    country: CountryCode.nullable(),
    /** The office ended on this day; `null`: still held, or not stated. */
    endedOn: isoDay.nullable(),
    /** For a family member or a close associate: the politically exposed person, by name. */
    relatedTo: words(200),
})
    .refine((s) => (s.status !== null && s.status !== 'none') ||
    [s.function, s.office, s.country, s.endedOn, s.relatedTo].every((v) => v === null), { message: 'details only with an office', path: ['status'] })
    .refine((s) => s.status === 'family_member' || s.status === 'close_associate' || !s.relatedTo, {
    message: 'a related person only for a family member or a close associate',
    path: ['relatedTo'],
})
    .meta({ id: 'PepStatement' });
/** Nothing stated yet - also what statements from before the question read as. */
export const PEP_NOT_STATED = {
    status: null,
    function: null,
    office: null,
    country: null,
    endedOn: null,
    relatedTo: null,
};
export const pepStatusLabel = {
    none: 'Kein politisch exponiertes Amt',
    pep: 'Hat ein politisch exponiertes Amt (oder hatte es)',
    family_member: 'Familienmitglied einer politisch exponierten Person',
    close_associate: 'Einer politisch exponierten Person bekanntermaßen nahestehend',
};
export const pepFunctionLabel = {
    government: 'Staats- oder Regierungschef, Minister, Staatssekretär, EU-Kommission',
    parliament: 'Abgeordnete, Mitglied eines Gesetzgebungsorgans',
    party_leadership: 'Führungsgremium einer politischen Partei',
    supreme_court: 'Oberstes Gericht, Verfassungsgericht oder anderes hohes Gericht',
    court_of_auditors: 'Leitungsorgan eines Rechnungshofs',
    central_bank: 'Leitungsorgan einer Zentralbank',
    diplomacy: 'Botschafter, Geschäftsträger, Verteidigungsattaché',
    state_enterprise: 'Organ eines staatseigenen Unternehmens',
    international_organisation: 'Leitung einer internationalen oder europäischen Organisation',
    other: 'Anderes Amt nach der EU-Liste',
};
/**
 * The statement in one line, as the firm reads it - in the app and in the GwG record: "not
 * stated" stays visible, never left out - an unanswered question is no "no". `day` writes a date.
 */
export function describePep(pep, day) {
    if (pep.status === null)
        return 'nicht angegeben';
    if (pep.status === 'none')
        return 'nein';
    const details = [
        pep.office ?? (pep.function ? pepFunctionLabel[pep.function] : null),
        pep.country,
        pep.endedOn && `bis ${day(pep.endedOn)}`,
        pep.relatedTo && `zu ${pep.relatedTo}`,
    ].filter(Boolean);
    return [pepStatusLabel[pep.status], ...details].join(' · ');
}
//# sourceMappingURL=pep.js.map